import React, { useMemo } from 'react';
import styled from 'styled-components';
import CopyIcon from '@/assets/icon/copy.svg?react';
import { truncateAddress } from '@/utils/address';
import EvmIcon from './assets/evm.svg?react';
import AppIcon from './assets/app.svg?react';
import { message, Tooltip } from 'antd';
import { AddressType } from '@/types/address';
import { SupportedChainId, SupportedChainMap, SupportedChains } from '@packet/constants/AppSupportChains';
import { useIsMobile } from '@packet/management';

export interface AddressItemProps {
  /** 地址字符串 */
  address: string;
  /** 地址类型 */
  type: AddressType;

  chainId: SupportedChainId;

  /** 是否截断地址显示 */
  truncate?: boolean;
  /** 截断配置 */
  truncateConfig?: {
    start: number;
    end: number;
  };
  /** 是否显示复制按钮 */
  showCopy?: boolean;
  hoverShowCopy?: boolean;
  /** 自定义样式类名 */
  className?: string;
  /** 点击复制时的回调 */
  onCopy?: (address: string) => void;
  /** 地址文本颜色 */
  color?: string;
}

/**
 * AddressItem 组件
 * 统一处理地址的显示格式，包括EVM和APP地址的图标
 */
export default function AddressItem({
  address,
  chainId,
  type,
  truncate = false,
  truncateConfig = { start: 6, end: 6 },
  showCopy = true,
  hoverShowCopy = false,
  className,
  onCopy,
  color,
}: AddressItemProps) {
  const { isMobile } = useIsMobile();
  const diff = useMemo(() => {
    const short = truncate ? truncateAddress(address, truncateConfig.start, truncateConfig.end) : address;

    if (type === AddressType.EVM) {
      const chain = SupportedChainMap[chainId] || SupportedChains[0];
      return {
        icon: <EvmIcon />,
        title: `${chain.name} Address`,
        address: (
          <a href={`${chain.blockExplorers.default.url}/address/${address}`} target="_blank">
            {short}
          </a>
        ),
      };
    }

    return { icon: <AppIcon />, title: 'Ocash Address', address: short };
  }, [type, address, truncate, truncateConfig, type, chainId]);
  // 复制功能
  const handleCopy = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!onCopy) {
      navigator.clipboard.writeText(address);
      message.success('Copied to clipboard');
      return;
    }
    onCopy(address);
    message.success('Copied to clipboard');
  };

  return (
    <AddressContainer className={className} $hoverShowCopy={hoverShowCopy}>
      <Tooltip title={diff.title}>
        <AddressIcon>{diff.icon}</AddressIcon>
      </Tooltip>

      <AddressText color={color}>{diff.address}</AddressText>

      {showCopy && (
        <CopyButton onClick={handleCopy} $hoverShowCopy={isMobile ? false : hoverShowCopy} className="copy_icon">
          <CopyIcon />
        </CopyButton>
      )}
    </AddressContainer>
  );
}

// Styled Components
const AddressContainer = styled.div<{ $hoverShowCopy?: boolean }>`
  display: flex;
  align-items: center;
  gap: 2px;
  &:hover {
    .copy_icon {
      ${(props) => props.$hoverShowCopy && 'opacity: 1;'}
    }
  }
`;

const AddressIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    height: auto;
    width: 20px;
  }
`;

const AddressText = styled.span<{ color?: string }>`
  color: ${(props) => props.color || '#141413'};
  word-break: break-all;
  line-height: 1.4;
  font-size: 16px;
  font-weight: 400;
`;

const CopyButton = styled.button<{ $hoverShowCopy?: boolean }>`
  background: none;
  border: none;
  cursor: pointer;
  opacity: 0.6;
  color: #141413;
  font-size: 16px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-left: 6px;
  ${(props) => props.$hoverShowCopy && 'opacity: 0;'}
  &:hover {
    opacity: 0.8;
  }
`;

export { AddressItem };
